import React, { useEffect, useState } from "react";
import { ICashTransferClient } from "../../types/cashTransferClient.type";
import { api } from "../../api/api";
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  Typography,
} from "antd";
import { useNavigate } from "react-router-dom";
import { DownOutlined } from "@ant-design/icons";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { PiMicrosoftExcelLogo } from "react-icons/pi";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";

const { Title } = Typography;
const { Search } = Input;

export default function CashTransferClients() {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [clients, setClients] = useState<ICashTransferClient[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [clientHasDebt, setClientHasDebt] = useState<boolean>(false);
  const [companyHasDebt, setCompanyHasDebt] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm();
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  
  // Расчет общего долга
  const totalClientDebtUSD = clients.reduce((acc, c) => acc + (c.totalClientDebt.USD || 0), 0);
  const totalClientDebtUZS = clients.reduce((acc, c) => acc + (c.totalClientDebt.UZS || 0), 0);
  const totalCompanyDebtUSD = clients.reduce((acc, c) => acc + (c.totalCompanyDebt.USD || 0), 0);
  const totalCompanyDebtUZS = clients.reduce((acc, c) => acc + (c.totalCompanyDebt.UZS || 0), 0);

  useEffect(() => {
    fetchClients();
  }, [page, pageSize, searchTerm, clientHasDebt, companyHasDebt]);

  const handleExport = () => {
    downloadFile("/export/cash-transfer/clients", "clients_data.xlsx");
  };

  const fetchClients = async () => {
    setLoading(true);

    try {
      const response = await api.get("/cash-transfer/clients", {
        params: {
          page,
          limit: pageSize,
          search: searchTerm,
          clientHasDebt: clientHasDebt ? true : undefined,
          companyHasDebt: companyHasDebt ? true : undefined,
        },
      });

      setClients(response.data.clients);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching clients", error);
      message.error("Ошибка при получение клиентов.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForm = async (values: { name: string }) => {
    setConfirmLoading(true);

    try {
      await api.post("/cash-transfer/client", values);
      message.success("Клиент успешно добавлен.");

      fetchClients();
      setIsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error(error);
      message.error(error.response.data.error);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const printClientsList = (clients: ICashTransferClient[]) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const totalClientDebtUSD = clients.reduce((acc, c) => acc + (c.totalClientDebt.USD || 0), 0);
    const totalClientDebtUZS = clients.reduce((acc, c) => acc + (c.totalClientDebt.UZS || 0), 0);
    const totalCompanyDebtUSD = clients.reduce((acc, c) => acc + (c.totalCompanyDebt.USD || 0), 0);
    const totalCompanyDebtUZS = clients.reduce((acc, c) => acc + (c.totalCompanyDebt.UZS || 0), 0);
    const debtorsCount = clients.filter(c => (c.totalClientDebt.USD || 0) > 0 || (c.totalClientDebt.UZS || 0) > 0).length;

    const tableHtml = `
      <table class="print-table" cellspacing="0" cellpadding="0">
        <thead>
          <tr>
            <th>№</th>
            <th>Имя</th>
            <th>Долг клиента USD</th>
            <th>Долг клиента UZS</th>
            <th>Наш долг USD</th>
            <th>Наш долг UZS</th>
          </tr>
        </thead>
        <tbody>
          ${clients.map((client, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${client.name || '&nbsp;'}</td>
              <td>${roundToThreeDecimals(client.totalClientDebt.USD) || '0'}</td>
              <td>${roundToThreeDecimals(client.totalClientDebt.UZS) || '0'}</td>
              <td>${roundToThreeDecimals(client.totalCompanyDebt.USD) || '0'}</td>
              <td>${roundToThreeDecimals(client.totalCompanyDebt.UZS) || '0'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;

    printWindow.document.write(`
      <html>
        <head>
          <title>Список клиентов конвертации</title>
          <style>
            * {
              margin: 0; padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 10px;
              background: #fff;
              padding: 12px;
            }
            .print-table {
              border-collapse: collapse;
              width: 100%;
              background: #fff;
            }
            .print-table th,
            .print-table td {
              border: 1px solid #000;
              padding: 4px 8px;
              text-align: left;
            }
            .print-table th {
              background-color: #f0f0f0;
            }
            .print-table tr:nth-child(even) {
              background-color: #fafafa;
            }

            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .print-table th {
                background-color: #f0f0f0 !important;
              }
              .print-table td, .print-table th {
                border: 1px solid #000 !important;
              }
            }
          </style>
        </head>
        <body>
          <h2 style="font-size:12px; margin-bottom:4px;">Список клиентов конвертации</h2>
          <div style="margin-bottom:6px; font-size:11px;"><b>Общий долг клиентов:</b> ${roundToThreeDecimals(totalClientDebtUSD)} USD / ${roundToThreeDecimals(totalClientDebtUZS)} UZS</div>
          <div style="margin-bottom:6px; font-size:11px;"><b>Общий наш долг:</b> ${roundToThreeDecimals(totalCompanyDebtUSD)} USD / ${roundToThreeDecimals(totalCompanyDebtUZS)} UZS</div>
          <div style="margin-bottom:10px; font-size:11px;"><b>Количество клиентов-должников:</b> ${debtorsCount}</div>
          ${tableHtml}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/cash-transfer/client/${id}`);

      message.success("Клиент успешно удален.");
      fetchClients();
    } catch (error: any) {
      message.error(error.response.data.error);
    }
  };

  const columns = [
    {
      title: "Имя",
      dataIndex: "name",
      key: "name",
      fixed: "left" as "left",
    },
    {
      title: "Долг клиента в USD",
      key: "clientDebtUSD",
      render: (_: any, client: ICashTransferClient) =>
        roundToThreeDecimals(client.totalClientDebt.USD),
    },
    {
      title: "Долг клиента в UZS",
      key: "clientDebtUSD",
      render: (_: any, client: ICashTransferClient) =>
        roundToThreeDecimals(client.totalClientDebt.UZS),
    },
    {
      title: "Наш долг в USD",
      key: "clientDebtUSD",
      render: (_: any, client: ICashTransferClient) =>
        roundToThreeDecimals(client.totalCompanyDebt.USD),
    },
    {
      title: "Наш долг в UZS",
      key: "clientDebtUSD",
      render: (_: any, client: ICashTransferClient) =>
        roundToThreeDecimals(client.totalCompanyDebt.UZS),
    },
    {
      title: "Создал",
      dataIndex: "ownerName",
      key: "ownerName",
    },
    {
      title: "",
      key: "actions",
      render: (_: any, client: ICashTransferClient) => {
        const menu = (
          <Menu>
            {authState.user?.role === UserRole.Admin && (
              <Menu.Item>
                <Popconfirm
                  title="Вы уверены, что хотите удалить этого клиента?"
                  onConfirm={() => handleDelete(client._id)}
                  okText="Да"
                  cancelText="Нет"
                >
                  Удалить
                </Popconfirm>
              </Menu.Item>
            )}
            <Menu.Item
              onClick={() =>
                navigate(`/cash-transfers/${client._id}/${client.name}`)
              }
            >
              Смотреть Конвертации
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];
  return (
    <div style={{ padding: 24 }}>
      <Title>Клиенты Обнал</Title>
      
      {/* Общий долг */}
      <div style={{ 
        background: '#f5f5f5', 
        padding: '16px', 
        borderRadius: '8px', 
        marginBottom: '16px',
        border: '1px solid #d9d9d9'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Общий долг клиентов:</div>
            <div style={{ color: '#1890ff' }}>
              USD: <span style={{ fontWeight: 'bold' }}>{roundToThreeDecimals(totalClientDebtUSD)}</span>
            </div>
            <div style={{ color: '#1890ff' }}>
              UZS: <span style={{ fontWeight: 'bold' }}>{roundToThreeDecimals(totalClientDebtUZS)}</span>
            </div>
          </div>
          
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Общий наш долг:</div>
            <div style={{ color: '#52c41a' }}>
              USD: <span style={{ fontWeight: 'bold' }}>{roundToThreeDecimals(totalCompanyDebtUSD)}</span>
            </div>
            <div style={{ color: '#52c41a' }}>
              UZS: <span style={{ fontWeight: 'bold' }}>{roundToThreeDecimals(totalCompanyDebtUZS)}</span>
            </div>
          </div>
          
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Чистый долг:</div>
            <div style={{ color: totalClientDebtUSD - totalCompanyDebtUSD >= 0 ? '#ff4d4f' : '#52c41a' }}>
              USD: <span style={{ fontWeight: 'bold' }}>{roundToThreeDecimals(totalClientDebtUSD - totalCompanyDebtUSD)}</span>
            </div>
            <div style={{ color: totalClientDebtUZS - totalCompanyDebtUZS >= 0 ? '#ff4d4f' : '#52c41a' }}>
              UZS: <span style={{ fontWeight: 'bold' }}>{roundToThreeDecimals(totalClientDebtUZS - totalCompanyDebtUZS)}</span>
            </div>
          </div>
        </div>
      </div>
      
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />
        <Checkbox
          checked={clientHasDebt}
          onChange={(e) => {
            setClientHasDebt(e.target.checked);
            setPage(1);
          }}
        >
          Клиент имеет долг
        </Checkbox>

        <Checkbox
          checked={companyHasDebt}
          onChange={(e) => {
            setCompanyHasDebt(e.target.checked);
            setPage(1);
          }}
        >
          У нас есть долг
        </Checkbox>
        <Button type="primary" onClick={() => setIsModalOpen(true)}>
          Добавить
        </Button>
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogo />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
        <Button
          type="default"
          onClick={() => printClientsList(clients)}
        >
          Печать
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={clients}
        rowKey={(record) => record._id}
        loading={loading}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title="Добавить клиента"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          <Form.Item
            name="name"
            label="Имя"
            rules={[{ required: true, message: "Введите имя" }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
